import type {
  Brand, Category, Discount, DiscountAppliesTo, DiscountAudience, DiscountScope, DiscountType, PriceList,
} from '@erp/shared';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Combobox, type ComboboxOption, FormField } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { listPriceLists } from '../../lib/pricing.js';
import { flattenCategoryOptions } from '../items/itemHelpers.js';

import { ItemPicker } from './ItemPicker.js';

interface FormState {
  name: string;
  scope: DiscountScope;
  targetId: string;
  /** Display label for the picked target — the product picker searches server-side and can't look one up by id. */
  targetLabel: string;
  discountType: DiscountType;
  discountValue: string;
  currencyCode: string;
  appliesTo: DiscountAppliesTo;
  audience: DiscountAudience;
  /** Only meaningful while `audience` is 'price_list'; kept across a mode flip so a mis-click doesn't erase the picks. */
  audiencePriceListIds: string[];
  minQty: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
}

const SELECT_CLASS = 'h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg';

/**
 * `<input type="date">` speaks calendar days, the rule window is stored as an
 * instant. Converting through the browser's own clock (rather than slicing the
 * ISO string, which is UTC) keeps "starts today" meaning today for the operator.
 */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Set equality, order-insensitive — used to tell whether the customer axis actually moved. */
function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

const startInstant = (day: string): string | null =>
  (day ? new Date(`${day}T00:00:00`).toISOString() : null);

/**
 * End-of-day, not midnight. The resolver keeps a rule while `ends_at >= now`, so
 * a bare date would silently switch the discount off at the START of the last day
 * the operator picked — the classic off-by-one nobody notices until a customer does.
 */
const endInstant = (day: string): string | null =>
  (day ? new Date(`${day}T23:59:59.999`).toISOString() : null);

export function DiscountFormPage({ edit = false }: { edit?: boolean }): JSX.Element {
  const { t } = useTranslation('discounts');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const tenant = useAuthStore((s) => s.tenant);

  const [brands, setBrands] = useState<Brand[] | null>(null);
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [priceLists, setPriceLists] = useState<PriceList[] | null>(null);
  /**
   * The customer axis EXACTLY as the server currently stores it, captured at
   * hydration. Two jobs, both about not losing a rule's target:
   *   * it decides whether the PATCH mentions the axis at all — see `onSubmit`;
   *   * it carries the names the rule was saved with, so a customer type that has
   *     since been deleted still shows its name instead of a bare uuid.
   */
  const [saved, setSaved] = useState<{ audience: DiscountAudience; ids: string[]; names: Record<string, string> } | null>(null);
  const [hydrated, setHydrated] = useState(!edit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    name: '',
    scope: 'product',
    targetId: '',
    targetLabel: '',
    discountType: 'percent',
    discountValue: '',
    currencyCode: tenant?.baseCurrencyCode ?? 'USD',
    appliesTo: 'both',
    audience: 'all',
    audiencePriceListIds: [],
    minQty: '',
    startsAt: '',
    endsAt: '',
    isActive: true,
  });

  // Brands and categories are small enough to filter in the browser; products
  // are not, which is why they get their own server-searched picker.
  useEffect(() => {
    void api().get<{ items: Brand[] }>('/brands')
      .then((r) => setBrands(r.data.items))
      .catch(() => setBrands([]));
    void api().get<{ items: Category[] }>('/categories')
      .then((r) => setCategories(r.data.items))
      .catch(() => setCategories([]));
    // Customer types ARE the price lists — the same groups that already decide
    // what these customers pay. Read-only here; a 403 (no pricing permission)
    // just means no fresh customer type can be picked, not a broken form.
    void listPriceLists().then(setPriceLists).catch(() => setPriceLists([]));
  }, []);

  useEffect(() => {
    if (!edit || !id) return;
    void api()
      .get<Discount>(`/discounts/${id}`)
      .then((r) => {
        const d = r.data;
        setForm({
          name: d.name,
          scope: d.scope,
          targetId: d.targetId ?? '',
          targetLabel: d.targetName ?? '',
          discountType: d.discountType,
          discountValue: d.discountValue,
          currencyCode: d.currencyCode ?? tenant?.baseCurrencyCode ?? 'USD',
          appliesTo: d.appliesTo,
          audience: d.audience,
          audiencePriceListIds: d.audiencePriceListIds,
          minQty: d.minQty ?? '',
          startsAt: toDateInput(d.startsAt),
          endsAt: toDateInput(d.endsAt),
          isActive: d.isActive,
        });
        setSaved({
          audience: d.audience,
          ids: d.audiencePriceListIds,
          // Names come back positionally aligned with the ids.
          names: Object.fromEntries(d.audiencePriceListIds.map((lid, i) => [lid, d.audiencePriceListNames[i] ?? ''])),
        });
        setHydrated(true);
      })
      .catch(() => {
        toast.error(tc('errors:server'));
        navigate('/sales/discounts');
      });
  }, [edit, id, navigate, tc, tenant?.baseCurrencyCode]);

  const brandOptions = useMemo<ComboboxOption[]>(
    () => (brands ?? [])
      .filter((b) => b.isActive)
      .map((b) => ({ value: b.id, label: b.title, keywords: b.title })),
    [brands],
  );
  const categoryOptions = useMemo(() => flattenCategoryOptions(categories ?? []), [categories]);

  /**
   * Only a LIVE customer type can be picked. `PricingService.listBasis` prices a
   * customer on a switched-off list as ungrouped, so a rule aimed at one could
   * never fire — and the server refuses to store it for exactly that reason.
   */
  const activeLists = useMemo(() => (priceLists ?? []).filter((l) => l.isActive), [priceLists]);
  const activeListIds = useMemo(() => new Set(activeLists.map((l) => l.id)), [activeLists]);
  /**
   * Customer types this rule already names that are no longer offerable — the
   * list was switched off (or deleted) after the rule was written. They are shown
   * and kept, never dropped behind the owner's back, but the server will reject
   * any SAVE that still names one, so they lock the rest of the picker.
   */
  const staleListIds = useMemo(
    () => form.audiencePriceListIds.filter((lid) => !activeListIds.has(lid)),
    [form.audiencePriceListIds, activeListIds],
  );

  if (!brands || !categories || !priceLists || !hydrated) {
    return (
      <Card className="p-6">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="mt-4 h-48 w-full" />
      </Card>
    );
  }

  const setField = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));

  const sectionTitle = (text: string): JSX.Element => (
    <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-fg-muted">{text}</h2>
  );

  /** Live name first; the name the rule was saved with is the fallback for a deleted list. */
  const listName = (listId: string): string =>
    (priceLists.find((l) => l.id === listId)?.name || saved?.names[listId] || t('form.unknownCustomerType'));

  /** Every customer type worth a checkbox: the live ones, plus whatever this rule already names. */
  const pickableListIds = [...activeLists.map((l) => l.id), ...staleListIds];

  const toggleList = (listId: string, on: boolean): void =>
    setField({
      audiencePriceListIds: on
        ? [...form.audiencePriceListIds, listId]
        : form.audiencePriceListIds.filter((x) => x !== listId),
    });

  // A rule can only be aimed at a customer type when one exists — unless it
  // already is, in which case hiding the option would silently retarget it.
  const canTargetLists = activeLists.length > 0 || form.audience === 'price_list';
  // A switched-off customer type can be KEPT but never re-saved alongside another
  // one, so the rest of the picker is frozen until it is unticked.
  const lockedByStale = staleListIds.length > 0;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form.name.trim() || !form.discountValue.trim()) {
      setError(t('form.errors.required'));
      return;
    }
    if (form.scope !== 'all' && !form.targetId) {
      setError(t('form.errors.target'));
      return;
    }
    if (form.discountType === 'percent') {
      const pv = Number(form.discountValue);
      if (!(pv > 0 && pv <= 100)) {
        setError(t('form.errors.percent'));
        return;
      }
    }
    if (form.discountType === 'fixed' && !form.currencyCode.trim()) {
      setError(t('form.errors.currency'));
      return;
    }
    if (form.audience === 'price_list' && form.audiencePriceListIds.length === 0) {
      setError(t('form.errors.customerTypes'));
      return;
    }
    if (form.startsAt && form.endsAt && form.startsAt > form.endsAt) {
      setError(t('form.errors.dates'));
      return;
    }
    setError(null);
    setSaving(true);
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      scope: form.scope,
      targetId: form.scope === 'all' ? null : form.targetId,
      discountType: form.discountType,
      discountValue: form.discountValue.trim(),
      // A percent is currency-agnostic; sending a code would only make the rule
      // look denominated in the list.
      currencyCode: form.discountType === 'fixed' ? form.currencyCode.trim().toUpperCase() : null,
      appliesTo: form.appliesTo,
      minQty: form.minQty.trim() || null,
      startsAt: startInstant(form.startsAt),
      endsAt: endInstant(form.endsAt),
      isActive: form.isActive,
    };
    // The customer axis rides along only when it actually MOVED. Repeating it
    // unchanged makes the server re-validate it, and it refuses a switched-off
    // customer type — which would leave a rule aimed at one uneditable forever,
    // unable even to fix a typo in its name. Omitting it keeps the stored set.
    const audienceIds = form.audience === 'price_list' ? form.audiencePriceListIds : [];
    if (!edit || !saved || saved.audience !== form.audience || !sameIds(saved.ids, audienceIds)) {
      payload.audience = form.audience;
      payload.audiencePriceListIds = audienceIds;
    }
    try {
      if (edit && id) await api().patch(`/discounts/${id}`, payload);
      else await api().post('/discounts', payload);
      navigate('/sales/discounts');
    } catch {
      setError(t('form.errors.server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <header className="flex items-center gap-3">
        <Link
          to="/sales/discounts"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-3 text-sm font-semibold text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden />
          {tc('actions.back', { defaultValue: 'Back' })}
        </Link>
        <h1 className="text-2xl font-semibold">{edit ? t('form.editTitle') : t('form.createTitle')}</h1>
      </header>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Card className="p-6">
          {sectionTitle(t('form.sections.basics'))}
          <FormField id="d-name" label={t('form.name')} required helperText={t('form.nameHint')}>
            <Input
              id="d-name"
              value={form.name}
              onChange={(e) => setField({ name: e.target.value })}
              maxLength={200}
            />
          </FormField>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.target'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="d-scope" label={t('form.scope')} required helperText={t('form.scopeHint')}>
              <select
                id="d-scope"
                className={SELECT_CLASS}
                value={form.scope}
                onChange={(e) => {
                  // The target id is polymorphic — a brand id is meaningless once
                  // the scope says "category", so switching always clears it.
                  setField({ scope: e.target.value as DiscountScope, targetId: '', targetLabel: '' });
                }}
              >
                <option value="product">{t('scope.product')}</option>
                <option value="brand">{t('scope.brand')}</option>
                <option value="category">{t('scope.category')}</option>
                <option value="all">{t('scope.all')}</option>
              </select>
            </FormField>

            {form.scope === 'product' && (
              <FormField id="d-target-product" label={t('form.target.product')} required>
                <ItemPicker
                  id="d-target-product"
                  value={form.targetId || null}
                  valueLabel={form.targetLabel || null}
                  onChange={(itemId, label) => setField({ targetId: itemId ?? '', targetLabel: label ?? '' })}
                  placeholder={t('form.targetPlaceholder.product')}
                  ariaLabel={t('form.target.product')}
                  emptyLabel={t('form.noResults')}
                />
              </FormField>
            )}
            {form.scope === 'brand' && (
              <FormField id="d-target-brand" label={t('form.target.brand')} required>
                <Combobox
                  id="d-target-brand"
                  options={brandOptions}
                  value={form.targetId || undefined}
                  onChange={(v, opt) => setField({ targetId: v ?? '', targetLabel: opt?.label ?? '' })}
                  placeholder={t('form.targetPlaceholder.brand')}
                  ariaLabel={t('form.target.brand')}
                />
              </FormField>
            )}
            {form.scope === 'category' && (
              <FormField id="d-target-category" label={t('form.target.category')} required>
                <Combobox
                  id="d-target-category"
                  options={categoryOptions.map((o) => ({ value: o.id, label: o.label }))}
                  value={form.targetId || undefined}
                  onChange={(v, opt) => setField({ targetId: v ?? '', targetLabel: opt?.label ?? '' })}
                  placeholder={t('form.targetPlaceholder.category')}
                  ariaLabel={t('form.target.category')}
                />
              </FormField>
            )}
            {form.scope === 'all' && (
              <p className="self-end text-xs text-fg-muted">{t('form.allScopeHint')}</p>
            )}
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.audience'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="d-audience" label={t('form.audience')} required helperText={t('form.audienceHint')}>
              <select
                id="d-audience"
                className={SELECT_CLASS}
                value={form.audience}
                onChange={(e) => {
                  const next = e.target.value as DiscountAudience;
                  // Landing on the targeted mode with nothing ticked would save a
                  // rule that matches nobody, so the first customer type comes
                  // pre-ticked. Existing picks are kept across a mode flip.
                  const first = activeLists[0];
                  setField({
                    audience: next,
                    audiencePriceListIds:
                      next === 'price_list' && form.audiencePriceListIds.length === 0 && first
                        ? [first.id]
                        : form.audiencePriceListIds,
                  });
                }}
              >
                <option value="all">{t('form.audienceOptions.all')}</option>
                <option value="price_list" disabled={!canTargetLists}>
                  {t('form.audienceOptions.price_list')}
                </option>
                <option value="no_list">{t('form.audienceOptions.no_list')}</option>
              </select>
            </FormField>

            {/* One hint in the spare column: why "normal" is a real group, or why
                the targeted option is greyed out. Never both, so the grid holds. */}
            {form.audience === 'no_list' ? (
              <p className="self-end text-xs text-fg-muted">{t('form.noListHint')}</p>
            ) : !canTargetLists ? (
              <p className="self-end text-xs text-fg-muted">{t('form.noCustomerTypes')}</p>
            ) : null}

            {form.audience === 'price_list' && (
              <fieldset className="md:col-span-2">
                <legend className="mb-1.5 text-sm font-medium text-fg">{t('form.customerTypes')}</legend>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {pickableListIds.map((listId) => {
                    const stale = staleListIds.includes(listId);
                    const checked = form.audiencePriceListIds.includes(listId);
                    return (
                      <label
                        key={listId}
                        className={`flex items-center gap-2 text-sm ${lockedByStale && !stale ? 'opacity-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={lockedByStale && !stale}
                          onChange={(e) => toggleList(listId, e.target.checked)}
                        />
                        {listName(listId)}
                        {stale && (
                          <span className="text-xs text-warning">({t('form.customerTypeOff')})</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-fg-muted">{t('form.customerTypesHint')}</p>
                {lockedByStale && (
                  <p className="mt-1.5 text-xs text-warning">
                    {t('form.customerTypeLockedHint', { names: staleListIds.map(listName).join(', ') })}
                  </p>
                )}
              </fieldset>
            )}
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.amount'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="d-type" label={t('form.discountType')} required>
              <select
                id="d-type"
                className={SELECT_CLASS}
                value={form.discountType}
                onChange={(e) => setField({ discountType: e.target.value as DiscountType })}
              >
                <option value="percent">{t('type.percent')}</option>
                <option value="fixed">{t('type.fixed')}</option>
              </select>
            </FormField>
            <FormField
              id="d-value"
              label={t('form.discountValue')}
              required
              helperText={form.discountType === 'percent' ? t('form.percentHint') : t('form.fixedHint')}
            >
              <Input
                id="d-value"
                value={form.discountValue}
                onChange={(e) => setField({ discountValue: e.target.value })}
                inputMode="decimal"
                placeholder={form.discountType === 'percent' ? '10' : '0.00'}
              />
            </FormField>
            {form.discountType === 'fixed' && (
              <FormField id="d-cur" label={t('form.currency')} required helperText={t('form.currencyHint')}>
                <CurrencySelect
                  id="d-cur"
                  value={form.currencyCode}
                  onChange={(c) => setField({ currencyCode: c })}
                />
              </FormField>
            )}
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.conditions'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="d-applies" label={t('form.appliesTo')} required helperText={t('form.appliesToHint')}>
              <select
                id="d-applies"
                className={SELECT_CLASS}
                value={form.appliesTo}
                onChange={(e) => setField({ appliesTo: e.target.value as DiscountAppliesTo })}
              >
                <option value="both">{t('appliesTo.both')}</option>
                <option value="retail">{t('appliesTo.retail')}</option>
                <option value="wholesale">{t('appliesTo.wholesale')}</option>
              </select>
            </FormField>
            <FormField id="d-minqty" label={t('form.minQty')} helperText={t('form.minQtyHint')}>
              <Input
                id="d-minqty"
                value={form.minQty}
                onChange={(e) => setField({ minQty: e.target.value })}
                inputMode="decimal"
                placeholder="0"
              />
            </FormField>
            <FormField id="d-start" label={t('form.startsAt')} helperText={t('form.datesHint')}>
              <Input
                id="d-start"
                type="date"
                value={form.startsAt}
                onChange={(e) => setField({ startsAt: e.target.value })}
              />
            </FormField>
            <FormField id="d-end" label={t('form.endsAt')}>
              <Input
                id="d-end"
                type="date"
                value={form.endsAt}
                onChange={(e) => setField({ endsAt: e.target.value })}
              />
            </FormField>
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.options'))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField({ isActive: e.target.checked })}
            />
            {t('form.isActive')}
          </label>
        </Card>

        {error && <p className="text-sm text-danger" role="alert">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" loading={saving}>{t('form.submit')}</Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/sales/discounts')} disabled={saving}>
            {tc('actions.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
